import { useAuth } from "@clerk/clerk-react";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { getMarkRange } from "@tiptap/core";
import { Editor as TipTapEditor } from "@tiptap/react";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import { OrganizationTemplateRubricSuggestion } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

import styles from "./styles";

interface LinkAccuracySuggestionPopupProps {
  editor: TipTapEditor;
  suggestion: OrganizationTemplateRubricSuggestion;
  onApplied?: (suggestionId: number, beforeHtml: string) => void;
  onDismiss: () => void | Promise<void>;
}

const LinkAccuracySuggestionPopup = ({
  editor,
  suggestion,
  onApplied,
  onDismiss,
}: LinkAccuracySuggestionPopupProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [destination, setDestination] = useState(
    suggestion.proposedReplacement ?? "",
  );

  useEffect(() => {
    setDestination(suggestion.proposedReplacement ?? "");
  }, [suggestion.id, suggestion.proposedReplacement]);

  const range = getHighlightRange(editor);
  const linkText = range
    ? editor.state.doc.textBetween(range.from, range.to)
    : suggestion.anchorText;
  const { href: currentHref, target } = range
    ? getLinkInRange(editor, range.from, range.to)
    : { href: "", target: "_blank" };

  const handleApply = async () => {
    const href = destination.trim();
    if (!href || !currentOrg?.id || !range) return;

    const beforeHtml = editor.getHTML();

    editor
      .chain()
      .focus()
      .setTextSelection({ from: range.from, to: range.to })
      .setLink({ href, target })
      .unsetMark("highlight")
      .run();

    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg.id}/organization-template-rubric-suggestions/suggestion/${suggestion.id}`,
      "PATCH",
      {},
      { status: "applied" },
    );

    onApplied?.(suggestion.id, beforeHtml);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setDestination(text.trim());
    } catch {
      // clipboard read can be blocked; user can still type the URL manually
    }
  };

  const canApply = destination.trim().length > 0;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.head}>
        <Box sx={styles.avatar}>
          <Box
            component="i"
            className="fa-solid fa-link-slash"
            sx={styles.avatarIcon}
          />
        </Box>
        <Box sx={styles.helperBlock}>
          <Typography sx={styles.helperText}>Bento helper</Typography>
          <Box sx={styles.kindRow}>
            <Box component="span" sx={styles.kindDotError} />
            Error · Link accuracy
          </Box>
        </Box>
        <IconButton size="small" sx={styles.closeButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.closeIcon}
          />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        <Typography sx={styles.title}>{suggestion.title}</Typography>
        <Typography sx={styles.rationale}>{suggestion.body}</Typography>

        <Box sx={styles.linkInfoCard}>
          <Box sx={styles.linkInfoRow}>
            <Box component="span" sx={styles.linkInfoLabel}>
              Link text
            </Box>
            <Box component="span" sx={styles.linkInfoText}>
              &ldquo;{linkText}&rdquo;
            </Box>
          </Box>
          <Box sx={styles.linkInfoRow}>
            <Box component="span" sx={styles.linkInfoLabel}>
              Points to
            </Box>
            <Box component="span" sx={styles.linkInfoUrlError}>
              {currentHref || "—"}
            </Box>
          </Box>
        </Box>

        <Typography sx={styles.fieldLabel}>Correct the destination</Typography>

        <Box sx={styles.inputWrapper}>
          <Box
            component="i"
            className="fa-solid fa-link"
            sx={styles.inputIcon}
          />
          <Box
            component="input"
            type="text"
            value={destination}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDestination(e.target.value)
            }
            placeholder="https://www.correctlink.com"
            sx={styles.input}
          />
          <ButtonBase sx={styles.pasteButton} onClick={handlePaste}>
            <Box
              component="i"
              className="fa-regular fa-clipboard"
              sx={styles.pasteButtonIcon}
            />
            Paste
          </ButtonBase>
        </Box>
      </Box>

      <Box sx={styles.actions}>
        <ButtonBase
          sx={canApply ? styles.primaryButton : styles.primaryButtonDisabled}
          onClick={canApply ? handleApply : undefined}
        >
          <Box
            component="i"
            className="fa-solid fa-check"
            sx={styles.primaryIcon}
          />
          Update link
        </ButtonBase>
        <ButtonBase sx={styles.rejectButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.rejectIcon}
          />
        </ButtonBase>
        <ButtonBase sx={styles.whyButton}>
          <Box
            component="i"
            className="fa-regular fa-circle-info"
            sx={styles.whyIcon}
          />
          Why?
        </ButtonBase>
      </Box>
    </Box>
  );
};

function getHighlightRange(
  editor: TipTapEditor,
): { from: number; to: number } | undefined {
  const highlightMark = editor.state.schema.marks.highlight;
  if (!highlightMark) return undefined;
  const { from } = editor.state.selection;
  const $pos = editor.state.doc.resolve(from);
  return getMarkRange($pos, highlightMark) ?? undefined;
}

function getLinkInRange(
  editor: TipTapEditor,
  from: number,
  to: number,
): { href: string; target: string } {
  let href = "";
  let target = "_blank";
  editor.state.doc.nodesBetween(from, to, (node) => {
    const linkMark = node.marks.find((m) => m.type.name === "link");
    if (linkMark) {
      href = linkMark.attrs.href ?? href;
      target = linkMark.attrs.target ?? target;
    }
  });
  return { href, target };
}

export default LinkAccuracySuggestionPopup;
